import {
  Button,
  DataTable,
  FlexBox,
  IconButton,
  Page,
  SearchInput,
  type SortStatus,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type { SortDirection } from 'lib-shared-types-api';
import { useCallback, useState } from 'react';

import {
  TemplateNormalized,
  templatesApi,
  TemplatesSortKey,
  TemplateTypes,
} from 'apis/templates';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { TEMPLATE_NAMES } from '../../../settingsTemplates.view';

import { CreateTemplateModal as CreateTemplateTranslationsModal } from './components/modals/multiLanguage/createTemplateModal';
import { EditTemplateModal as EditTemplateTranslationsModal } from './components/modals/multiLanguage/editTemplateModal';
import { CreateTemplateModal } from './components/modals/singleLanguage/createTemplateModal';
import { EditTemplateModal } from './components/modals/singleLanguage/editTemplateModal';
import { TemplateLanguagesCell } from './templateLanguagesCell';
import { normalizeTemplate } from './utils';

interface TemplateTabProps {
  templateType: TemplateTypes;
  sectionTitle: string;
}

export const TemplatesTab = ({
  templateType,
  sectionTitle,
}: TemplateTabProps): JSX.Element => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState<
    TemplateNormalized | undefined
  >();
  const [sortStatus, setSortStatus] = useState<SortStatus | null>({
    sortBy: 'name',
    direction: 'asc',
  });

  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const flags = useFeatureFlag(['templates-language-translation-control']);

  const {
    asyncStatus,
    hasNextPage,
    hasPreviousPage,
    items,
    changeArg,
    nextPage,
    previousPage,
    refetchItems,
    updateItem,
    search,
    arg,
  } = usePaginatedEndpoint({
    endpoint: templatesApi.endpoints.readAllTemplatesPaginated,
    initialArg: {
      limit: 25,
      offset: 0,
      type: templateType,
      sort: { key: 'name', direction: 'asc' },
    },
    itemsSelector: ({ data }) => {
      return data.templates;
    },
  });

  const [deleteTemplate] = templatesApi.useDeleteTemplateMutation();

  const handleEdit = (template) => {
    ampli.settingsTemplatesEditTemplateButtonClicked({
      templateType,
      templateName: template.name,
    });
    setIsAddModalOpen(true);
    setCurrentTemplate(normalizeTemplate(template));
  };

  const handleEditConfirmed = (template) => {
    updateItem(
      template,
      (thisTemplate, otherTemplate) => thisTemplate.id === otherTemplate.id,
    );
  };

  const handleRemove = useCallback(
    async (template) => {
      ampli.settingsTemplatesRemoveTemplateButtonClicked({
        templateType,
        templateName: template?.name,
      });

      const { isConfirmed, confirm } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: 'Are you sure?',
              description: `Are you sure you want to remove the template
        "${template?.name}"?`,
              confirm: {
                label: 'Delete',
                async: true,
                asyncLabel: 'Deleting...',
              },
            },
          },
        }),
      );

      if (isConfirmed) {
        try {
          confirm(async () => {
            await deleteTemplate({
              templateId: template.id,
            }).unwrap();

            refetchItems();

            ampli.settingsTemplatesRemoveTemplateConfirmed({
              templateType,
              templateName: template?.name,
            });

            notify(
              {
                title: 'Template deleted',
                text: `"${template.name}" template has been deleted`,
                icon: 'i-done',
                className: 'success',
                time: 3000,
              },
              NotificationType.Success,
            );
          });
        } catch {
          notify(
            {
              title: 'An error occurred',
              text: `An error occurred when trying to delete the "${template.name}" template`,
              icon: 'i-exclamation',
              className: 'danger',
            },
            NotificationType.Error,
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTemplate?.name, deleteTemplate, dispatch, notify, templateType],
  );

  const isEditMode = !!currentTemplate;

  const handleSearchSubmit = (searchTerm) => {
    search(searchTerm);
  };

  const handleSearchToggle = (isOpen: boolean) => {
    if (!isOpen && arg?.searchTerm) {
      search('');
    }
  };

  const handleAddModalClose = async (canceled = false) => {
    setIsAddModalOpen(false);
    setCurrentTemplate(undefined);

    if (!canceled) {
      refetchItems();
    }
  };

  const handleChangeSort = (status: SortStatus | null): void => {
    setSortStatus(status);

    changeArg({
      type: templateType,
      sort: status
        ? {
            key: status.sortBy as TemplatesSortKey,
            direction: status.direction as SortDirection,
          }
        : { key: 'name', direction: 'asc' },
      offset: 0,
    });
  };

  const handleShowAddModal = () => {
    ampli.settingsTemplatesAddTemplateButtonClicked({
      templateType,
    });

    setIsAddModalOpen(true);
  };

  const handleCreate = () => {
    refetchItems();
  };

  return (
    <>
      <Page.Section
        title={sectionTitle}
        controls={
          <>
            <SearchInput
              onSubmit={handleSearchSubmit}
              onToggle={handleSearchToggle}
            />
            <Button onClick={handleShowAddModal}>
              {`Add ${TEMPLATE_NAMES[templateType].toLowerCase()}`}
            </Button>
          </>
        }
      >
        <DataTable
          sortingProps={{
            sortStatus,
            onSortChange: handleChangeSort,
          }}
          columns={[
            {
              columnId: 'name',
              label: 'Name',
              selector: ({ name }) => name,
              sortable: true,
            },
            {
              columnId: 'created_at',
              label: 'Created',
              selector: ({ created_at }) => {
                return <Timestamp relative time={created_at} />;
              },
              sortable: true,
            },
            {
              columnId: 'last_edited_at',
              label: 'Updated',
              selector: ({ last_edited_at }) => {
                if (last_edited_at) {
                  return <Timestamp relative time={last_edited_at} />;
                }
                return '-';
              },
              sortable: true,
            },
            ...(flags.templatesLanguageTranslationControl
              ? [
                  {
                    label: 'Languages',
                    selector: ({ translations }) => {
                      return (
                        <TemplateLanguagesCell translations={translations} />
                      );
                    },
                  },
                ]
              : []),
            {
              label: '',
              selector: (template) => (
                <FlexBox justifyContent="flex-end">
                  <IconButton
                    variant="tertiary"
                    icon="pen"
                    onClick={() => handleEdit(template)}
                  />

                  <IconButton
                    variant="tertiary"
                    icon="trash"
                    onClick={() => handleRemove(template)}
                  />
                </FlexBox>
              ),
              style: {
                width: 0,
              },
            },
          ]}
          data={items ?? []}
          tableStatusProps={{
            loading: asyncStatus === 'pending',
            loadingFailed: asyncStatus === 'rejected',
            onLoadingRetry: refetchItems,
          }}
          paginationProps={{
            previousButtonClick: previousPage,
            previousButtonDisabled: !hasPreviousPage,
            nextButtonClick: nextPage,
            nextButtonDisabled: !hasNextPage,
            loading: asyncStatus === 'pending',
          }}
        />
      </Page.Section>

      {isAddModalOpen &&
        flags.templatesLanguageTranslationControl &&
        isEditMode && (
          <EditTemplateTranslationsModal
            templateType={templateType}
            isModalOpen={isAddModalOpen}
            onClose={handleAddModalClose}
            selectedTemplate={currentTemplate as TemplateNormalized}
            onEditConfirmed={handleEditConfirmed}
          />
        )}
      {isAddModalOpen &&
        flags.templatesLanguageTranslationControl &&
        !isEditMode && (
          <CreateTemplateTranslationsModal
            templateType={templateType}
            isModalOpen={isAddModalOpen}
            onClose={handleAddModalClose}
            onCreate={handleCreate}
          />
        )}

      {isAddModalOpen &&
        !flags.templatesLanguageTranslationControl &&
        isEditMode && (
          <EditTemplateModal
            templateType={templateType}
            isModalOpen={isAddModalOpen}
            onClose={handleAddModalClose}
            selectedTemplate={currentTemplate as TemplateNormalized}
            onEditConfirmed={handleEditConfirmed}
          />
        )}

      {isAddModalOpen &&
        !flags.templatesLanguageTranslationControl &&
        !isEditMode && (
          <CreateTemplateModal
            templateType={templateType}
            isModalOpen={isAddModalOpen}
            onClose={handleAddModalClose}
            onCreate={handleCreate}
          />
        )}
    </>
  );
};
